import type { PoolMessage } from '#cmui/types/poolDetail'
import sanitizeHtml from 'sanitize-html'
import usePoolStore from '~/store/pool'
import { MessageTypes, type PoolPhoto, type PoolStation } from '#cmui/types'
import {
    PoolPanelState as DrawerState,
    type Pool
} from '~/interfaces/poolDetail'

export default function useMapDrawer() {
    const loadingPoolPricesModal = ref(false)
    const loadingInfo = ref(false)
    const loadingPictures = ref(false)
    const loadingStations = ref(false)

    const drawerVisible = ref(false)
    const currentDrawerState = ref(DrawerState.Preview)
    const showPreviewPicture = ref(false)
    const picturePreviewIndex = ref(0)
    const poolDetails: Ref<Pool | undefined> = ref(undefined)
    const poolStations: Ref<PoolStation[] | undefined> = ref([])
    const pictures: Ref<PoolPhoto[] | undefined> = ref([])
    const message: Ref<PoolMessage | undefined> = ref(undefined)

    const poolStore = usePoolStore()

    const getPoolDetails = async (slug: string) => {
        const { fetchPoolDetailFromPoolDetail, setPoolSlug } = poolStore

        setPoolSlug(slug)
        await fetchPoolDetailFromPoolDetail()

        poolDetails.value = usePoolStore().poolDetail as Pool

        // when it's finished, we set the loading to false
        loadingInfo.value = false

        if (poolDetails.value) {
            // This allows us to prevent XSS attacks (because the description is put in a v-html)
            poolDetails.value.description = sanitizeHtml(
                poolDetails.value.description || '',
                {
                    allowedTags: []
                }
            )
        }
    }

    const getPoolMessage = () => {
        const { getWarningMessage } = poolStore

        if (!poolDetails.value || !getWarningMessage) return

        message.value = {
            action: '',
            content: getWarningMessage,
            type: MessageTypes.WARNING
        } // TODO: this will need to change, once the API is updated
    }

    const getPoolStations = async () => {
        if (!poolDetails.value) return

        const { fetchPoolStationsFromCommunity } = poolStore

        await fetchPoolStationsFromCommunity()

        poolStations.value = usePoolStore().poolStations as PoolStation[]

        loadingStations.value = false
    }

    const getPoolPictures = async () => {
        if (!poolDetails.value) {
            loadingPictures.value = false

            return
        }

        const { fetchPoolPhotos } = poolStore
        await fetchPoolPhotos()

        pictures.value = poolStore.poolPhotos || []

        loadingPictures.value = false
    }

    const getPoolData = async (slug: string) => {
        // We put all the loadings to true, each function will set it to false when it's done
        loadingInfo.value = true
        loadingPictures.value = true
        loadingStations.value = true
        poolDetails.value = undefined
        // We need to await the pool details to get the pool id
        await getPoolDetails(slug)

        if (!poolDetails.value) {
            loadingInfo.value = false
            loadingPictures.value = false
            loadingStations.value = false
        }

        getPoolMessage()
        await getPoolStations()
        await getPoolPictures()
    }

    const openPicture = (index: number) => {
        picturePreviewIndex.value = index
        showPreviewPicture.value = true
    }

    const setDrawerState = (state: DrawerState) => {
        currentDrawerState.value = state
    }

    return {
        drawerVisible,
        currentDrawerState,
        showPreviewPicture,
        loadingPoolPricesModal,
        picturePreviewIndex,
        poolDetails,
        poolStations,
        pictures,
        loadingInfo,
        loadingPictures,
        loadingStations,
        message,

        // methods
        openPicture,
        setDrawerState,
        getPoolData,
        getPoolPictures,
        getPoolDetails,
        getPoolMessage,
        getPoolStations
    }
}
